import React, { useEffect, useRef, useState } from 'react';
import { SubjectSiteAccount } from 'api/subjectSiteApi';
import { AppPath } from 'appConstants';
import Logo from 'images/subjectSite/GetSet4Edu_Logo.png';
import MenuIcon from 'images/subjectSite/Icon_BtnMobileMenu_Black.png';
import {
  SIGN_OUT_LINK,
  SubjectSiteLink,
  onEnterOrSpace,
} from './subjectSiteMenu';
import SubjectSiteMenuLink from './SubjectSiteMenuLink';
import { useSlideToggle } from './useSlideToggle';

type Props = {
  menuLinks: SubjectSiteLink[];
  account: SubjectSiteAccount | null;
  /**
   * Loading has ended, so the school menu and Logout show even when the
   * school's details could not be loaded
   */
  bAccountLoaded: boolean;
  schoolLinks: SubjectSiteLink[];
  accountLinks: SubjectSiteLink[];
  onLessonMenuOpen: (() => void) | null;
  onSideMenuOpen: () => void;
};

const SCHOOL_MENU_CLOSE_DELAY_MILLISECONDS = 300;
const SCHOOL_MENU_SLIDE_MILLISECONDS = 200;

/**
 * The subject site's header: the menu button, logos, the subject's links and
 * the school's name, which opens its links while the pointer is over it
 */
const SubjectSiteHeader: React.FC<Props> = ({
  menuLinks,
  account,
  bAccountLoaded,
  schoolLinks,
  accountLinks,
  onLessonMenuOpen,
  onSideMenuOpen,
}) => {
  const [bSchoolMenuOpen, setBSchoolMenuOpen] = useState(false);
  const closeTimeout = useRef<number | undefined>(undefined);
  const { ref: schoolMenuRef, shown: bSchoolMenuShown } =
    useSlideToggle<HTMLUListElement>(
      bSchoolMenuOpen,
      bSchoolMenuOpen ? SCHOOL_MENU_SLIDE_MILLISECONDS : 0
    );

  useEffect(() => () => window.clearTimeout(closeTimeout.current), []);

  const openSchoolMenu = () => {
    window.clearTimeout(closeTimeout.current);
    setBSchoolMenuOpen(true);
  };

  const closeSchoolMenuSoon = () => {
    window.clearTimeout(closeTimeout.current);
    closeTimeout.current = window.setTimeout(
      () => setBSchoolMenuOpen(false),
      SCHOOL_MENU_CLOSE_DELAY_MILLISECONDS
    );
  };

  const schoolMenuLinkProps = {
    className: 'Level2',
    onMouseOver: openSchoolMenu,
    onMouseOut: closeSchoolMenuSoon,
    onFocus: openSchoolMenu,
    onBlur: closeSchoolMenuSoon,
  };

  return (
    <div className="MainHeader MHBlack">
      <div className="WidthContent">
        <div className="BtnMobileMenu">
          <div
            className="BMMBlack"
            role="button"
            tabIndex={0}
            aria-label="Open the menu"
            onClick={onSideMenuOpen}
            onKeyDown={onEnterOrSpace(onSideMenuOpen)}
          >
            <img src={MenuIcon} alt="Menu button" />
          </div>
        </div>

        {account?.logoUrl && (
          <div className="SchoolLogo">
            <img src={account.logoUrl} alt="School Logo" />
          </div>
        )}

        <div className="MainLogo">
          <div className="Logo LogoEduBlack">
            <SubjectSiteMenuLink
              link={{ name: '', url: AppPath.DASHBOARD, bInApp: true }}
            >
              <img src={Logo} alt="GetSet4Education" />
            </SubjectSiteMenuLink>
          </div>
          <div className="Logo LogoEdu">
            <img src={Logo} alt="GetSet4Education" />
          </div>
        </div>

        <div className="Menu">
          <div className="LeftMenu Navigation">
            <ul className="Level1">
              {onLessonMenuOpen && (
                <li className="Level1">
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <a
                    className="Level1"
                    href="#"
                    onClick={(event) => {
                      event.preventDefault();
                      onLessonMenuOpen();
                    }}
                  >
                    Lesson Plans
                  </a>
                </li>
              )}
              {menuLinks.map((link) => (
                <li className="Level1" key={link.name}>
                  <SubjectSiteMenuLink link={link} className="Level1" />
                </li>
              ))}
            </ul>
          </div>

          <div className="RightMenu Navigation">
            <ul className="Level1">
              {bAccountLoaded && (
                <li
                  className={`Level1${
                    bSchoolMenuOpen ? ' Level1Highlighted' : ''
                  }`}
                >
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <a
                    className="Level1 SchoolName"
                    href="#"
                    aria-haspopup="true"
                    aria-expanded={bSchoolMenuOpen}
                    onClick={(event) => event.preventDefault()}
                    onMouseOver={openSchoolMenu}
                    onMouseOut={closeSchoolMenuSoon}
                    onFocus={openSchoolMenu}
                    onBlur={closeSchoolMenuSoon}
                  >
                    <span className="NameTxt">{account?.schoolName}</span>
                    <span className="Nav_Arrow"></span>
                  </a>
                  <ul
                    ref={schoolMenuRef}
                    className="Level2"
                    style={bSchoolMenuShown ? { display: 'block' } : undefined}
                  >
                    {[...schoolLinks, ...accountLinks].map((link) => (
                      <li className="Level2" key={link.name}>
                        <SubjectSiteMenuLink
                          link={link}
                          {...schoolMenuLinkProps}
                        />
                      </li>
                    ))}
                    <li className="Level2">
                      <SubjectSiteMenuLink
                        link={SIGN_OUT_LINK}
                        {...schoolMenuLinkProps}
                        className="Level2 BtnLogout"
                      >
                        <span>Logout</span>
                      </SubjectSiteMenuLink>
                    </li>
                  </ul>
                </li>
              )}
            </ul>
          </div>

          <div className="BottomFix"></div>
        </div>
      </div>
    </div>
  );
};

export default SubjectSiteHeader;
